import { useEffect, useRef, useState } from "react";

import type { EditorImage, MosaicRegion } from "../editor/types";
import { composeSourceImage } from "../utils/files";
import { useMosaicProcessor } from "./useMosaicProcessor";

interface PreviewState {
  imageId: string | null;
  bitmap: ImageBitmap | null;
  revision: number;
}

function sourceKey(image: EditorImage): string {
  return `${image.id}:${image.restorations.map((patch) => `${patch.id}@${patch.strength}`).join(",")}`;
}

/**
 * Renders the active image with its effects in a worker. `draft` is the range
 * still being drawn, so the result is visible while the pointer is down.
 */
export function useEffectPreview(
  image: EditorImage | undefined,
  draft: MosaicRegion | null,
  onError: (message: string) => void,
) {
  const process = useMosaicProcessor();
  const [state, setState] = useState<PreviewState>({ imageId: null, bitmap: null, revision: 0 });
  const sourceRef = useRef<{ key: string; data: ImageData } | null>(null);
  const requestRef = useRef(0);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!image) return;
    const requestId = ++requestRef.current;
    const stale = () => requestId !== requestRef.current;
    void (async () => {
      try {
        const key = sourceKey(image);
        if (sourceRef.current?.key !== key) {
          sourceRef.current = { key, data: await composeSourceImage(image) };
        }
        const source = sourceRef.current.data;
        const regions = draft ? [...image.regions, draft] : image.regions;
        const output =
          regions.length === 0 && image.coverage === "inside"
            ? source
            : await process(source, {
                regions,
                coverage: image.coverage,
                coverageEffect: image.coverageEffect,
              });
        if (stale()) return;
        const bitmap = await createImageBitmap(output);
        if (stale()) {
          bitmap.close();
          return;
        }
        setState((current) => ({
          imageId: image.id,
          bitmap,
          revision: current.revision + 1,
        }));
      } catch (error) {
        if (!stale()) {
          onErrorRef.current(
            error instanceof Error ? error.message : "プレビューを更新できませんでした",
          );
        }
      }
    })();
  }, [draft, image, process]);

  // Close a bitmap only once the canvas has moved on to its replacement.
  const shownRef = useRef<ImageBitmap | null>(null);
  useEffect(() => {
    const previous = shownRef.current;
    shownRef.current = state.bitmap;
    if (previous && previous !== state.bitmap) previous.close();
  }, [state.bitmap]);
  useEffect(() => () => shownRef.current?.close(), []);

  const current = state.imageId === image?.id;
  return {
    bitmap: current ? state.bitmap : null,
    revision: state.revision,
  };
}
